import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Save, Ban, KeyRound, Trash2 } from 'lucide-react';
import { adminUpdateUser } from '../lib/api';

/** Comprehensive admin user-editor modal.
 * Lets owners change role, plan, profile_type, tier, ban, name, storage override.
 * Admins (non-owner) are limited — backend re-enforces.
 */
export default function AdminUserEditor({ user, currentUser, isOwner, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: user?.name || '',
    role: user?.role || 'creator',
    profile_type: user?.profile_type || 'creator',
    plan: user?.plan || 'free',
    reapercast_tier: user?.reapercast_tier || 'free',
    storage_quota_mb_override: user?.storage_quota_mb_override || '',
    banned: !!user?.banned,
  }));
  const [saving, setSaving] = useState(false);
  const isSelf = currentUser?.id === user?.id;
  const isOwnerTarget = user?.role === 'owner';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!user) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (form.name !== user.name) body.name = form.name;
      if (isOwner && form.role !== user.role) body.role = form.role;
      if (form.profile_type !== user.profile_type) body.profile_type = form.profile_type;
      if (form.plan !== user.plan) body.plan = form.plan;
      if (form.reapercast_tier !== user.reapercast_tier) body.reapercast_tier = form.reapercast_tier;
      if (isOwner) {
        const so = form.storage_quota_mb_override === '' ? null : parseInt(form.storage_quota_mb_override, 10);
        if (so !== (user.storage_quota_mb_override ?? null)) body.storage_quota_mb_override = so ?? 0;
      }
      if (form.banned !== !!user.banned && !isSelf && !isOwnerTarget) body.banned = form.banned;
      if (Object.keys(body).length === 0) { toast('No changes'); setSaving(false); return; }
      const resp = await adminUpdateUser(user.id, body);
      onSaved && onSaved(resp.user);
      toast.success('User updated');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3 md:p-6" onClick={onClose} data-testid="admin-user-editor">
      <div className="card-shell w-full max-w-[640px] p-5 md:p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-3 mb-4 sticky top-0 bg-[var(--theme-card)] z-10 pb-3 border-b border-white/5 -mx-5 md:-mx-6 px-5 md:px-6 -mt-5 md:-mt-6 pt-5 md:pt-6">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-bold text-white/55">Edit user</div>
            <h2 className="font-display text-[22px] truncate">{user.name || user.email}</h2>
            <div className="text-[11.5px] text-white/55 truncate">{user.email}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" aria-label="Close"><X className="w-4 h-4" /></button>
        </header>

        <Field label="Display name">
          <input value={form.name} onChange={e => set('name', e.target.value)} className="input-base" data-testid="admin-edit-name" />
        </Field>

        <Row>
          <Field label="Role" hint={isOwner ? '' : 'Owner-only'}>
            <select disabled={!isOwner} value={form.role} onChange={e => set('role', e.target.value)} className="input-base" data-testid="admin-edit-role">
              <option value="creator">Creator</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </Field>
          <Field label="Profile type">
            <select value={form.profile_type} onChange={e => set('profile_type', e.target.value)} className="input-base" data-testid="admin-edit-profile-type">
              <option value="creator">Creator</option>
              <option value="musician">Musician</option>
              <option value="listener">Listener</option>
              <option value="both">Both</option>
            </select>
          </Field>
        </Row>

        <Row>
          <Field label="Subscription plan" hint="Storage + features">
            <select value={form.plan} onChange={e => set('plan', e.target.value)} className="input-base" data-testid="admin-edit-plan">
              <option value="free">Free</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
              <option value="studio">Studio</option>
            </select>
          </Field>
          <Field label="ReaperCast tier" hint="Academy / specialists">
            <select value={form.reapercast_tier} onChange={e => set('reapercast_tier', e.target.value)} className="input-base" data-testid="admin-edit-rctier">
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="label">Label</option>
            </select>
          </Field>
        </Row>

        {isOwner && (
          <Field label="Storage override (MB)" hint="Leave blank for plan default">
            <input
              type="number"
              value={form.storage_quota_mb_override}
              onChange={e => set('storage_quota_mb_override', e.target.value)}
              placeholder="e.g. 51200 = 50 GB"
              className="input-base"
              data-testid="admin-edit-storage" />
          </Field>
        )}

        {!isSelf && !isOwnerTarget && (
          <label className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-white/[0.03] border border-white/8 cursor-pointer hover:bg-white/[0.05]">
            <input type="checkbox" checked={form.banned} onChange={e => set('banned', e.target.checked)} data-testid="admin-edit-banned" />
            <Ban className="w-4 h-4 text-[#ff6b8a]" />
            <span className="text-[13px]">Banned <span className="text-white/45">— blocks login + every authed request</span></span>
          </label>
        )}

        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost text-[12.5px]">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-[12.5px]" data-testid="admin-edit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between mb-1.5">
        <label className="text-[11px] uppercase tracking-wider font-bold text-white/55">{label}</label>
        {hint && <span className="text-[10.5px] text-white/40">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
